import { DependencyList, useEffect, useRef } from 'react';

export interface UseDebouncedEffectHookOptions {
  /**
   * The amount of milliseconds that the hook should wait
   * to debounce any changes to its dependency list.
   */
  debounceTimeout: number;

  /**
   * Whether or not the hook should execute the effect
   * on mount AND on debounced changes to the dependency list,
   * OR just on debounced changes to the dependency list.
   */
  skipEffectOnMount?: boolean;
}

/**
 * React component hook that executes `effectFn` each time
 * `dependencyList` changes between render cycles. This hook
 * uses trailing debouncing, and can be used to limit the
 * amount of side-effects produced by changes to `dependencyList`.
 *
 * Please note: unlike `React.useEffect`, the function passed to
 * `effectFn` cannot return a cleanup function.
 */
export const useDebouncedEffect = (
  effectFn: () => void,
  dependencyList: DependencyList,
  options: UseDebouncedEffectHookOptions,
) => {
  // Ref for keeping track of whether this is the first render cycle
  const didMountRef = useRef(false);

  // Ref for keeping track of the function to cancel in-progress debounce timers
  const currentTimerCancellationFn = useRef(() => {
    // noop
  });

  useEffect(() => {
    if (didMountRef.current || !options.skipEffectOnMount) {
      const timer = setTimeout(effectFn, options.debounceTimeout);
      currentTimerCancellationFn.current = () => clearTimeout(timer);
    }

    didMountRef.current = true;

    // If the dependency list changes, cancel the current timer
    return currentTimerCancellationFn.current;
  }, [...dependencyList, options.debounceTimeout]);

  // Return a function allowing the component to cancel in-progress debounce timers outside of the render cycle
  return currentTimerCancellationFn.current;
};
